import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MemberMeeting } from '../../auth/memberApi';
import { useAuth } from '../../auth/AuthContext';
import './index.css';

const meetingTime = (meeting: MemberMeeting) => {
  const start = new Date(meeting.startAt);
  if (Number.isNaN(start.getTime())) return meeting.startAt || 'Time unavailable';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(start);
};

const Meetings = () => {
  const { listMeetings, cancelMeeting } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<MemberMeeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeBookingUid, setActiveBookingUid] = useState('');
  const [error, setError] = useState('');

  const loadMeetings = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setMeetings(await listMeetings());
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load meetings.');
    } finally {
      setIsLoading(false);
    }
  }, [listMeetings]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const { upcoming, previous } = useMemo(() => {
    const now = Date.now();
    const isUpcoming = (meeting: MemberMeeting) => (
      new Date(meeting.startAt).getTime() >= now
      && meeting.status.toUpperCase() !== 'CANCELLED'
      && !meeting.isDeleted
    );
    return {
      upcoming: meetings.filter(isUpcoming).sort((a, b) => a.startAt.localeCompare(b.startAt)),
      previous: meetings.filter((meeting) => !isUpcoming(meeting)),
    };
  }, [meetings]);

  const performAction = async (meeting: MemberMeeting, action: 'cancel' | 'reschedule') => {
    if (action === 'cancel' && !window.confirm(`Cancel “${meeting.title}”?`)) return;
    setActiveBookingUid(meeting.bookingUid);
    setError('');
    try {
      await cancelMeeting(meeting.bookingUid, action);
      await loadMeetings();
      if (action === 'reschedule') {
        const selectNewTime = window.confirm(
          'Your previous meeting time was cancelled. Select OK to choose a new time.',
        );
        if (selectNewTime) {
          navigate('/schedule', { state: { calendarRefresh: Date.now() } });
        }
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update the meeting.');
    } finally {
      setActiveBookingUid('');
    }
  };

  const renderMeetings = (items: MemberMeeting[], emptyMessage: string) => {
    if (!items.length) return <p className="meetings-empty">{emptyMessage}</p>;
    return (
      <div className="meetings-list">
        {items.map((meeting) => {
          const canManage = (
            new Date(meeting.startAt).getTime() >= Date.now()
            && meeting.status.toUpperCase() !== 'CANCELLED'
            && !meeting.isDeleted
          );
          return (
            <article className="meeting-row" key={meeting.bookingUid}>
              <div>
                <span className="meeting-status">{meeting.status.replaceAll('_', ' ')}</span>
                <h3>{meeting.title}</h3>
                <p>{meetingTime(meeting)}</p>
                <small>{meeting.timezone}</small>
              </div>
              {canManage && (
                <div className="meeting-actions" aria-label={`Actions for ${meeting.title}`}>
                  <button
                    type="button"
                    onClick={() => performAction(meeting, 'cancel')}
                    disabled={Boolean(activeBookingUid)}
                  >
                    {activeBookingUid === meeting.bookingUid ? 'Working…' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={() => performAction(meeting, 'reschedule')}
                    disabled={Boolean(activeBookingUid)}
                  >
                    Reschedule
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>
    );
  };

  return (
    <section className="meetings-page">
      <header className="meetings-header">
        <p>Appointments</p>
        <h1>Your meetings.</h1>
        <span>Review upcoming appointments and your previous meeting history.</span>
      </header>

      <div className="meetings-toolbar">
        <span>{isLoading ? 'Loading meetings…' : `${meetings.length} meeting${meetings.length === 1 ? '' : 's'}`}</span>
        <button type="button" onClick={loadMeetings} disabled={isLoading}>Refresh</button>
      </div>

      {error && <p className="meetings-error" role="alert">{error}</p>}
      {!isLoading && !error && (
        <>
          <section className="meetings-group" aria-labelledby="upcoming-meetings">
            <h2 id="upcoming-meetings">Upcoming meetings</h2>
            {renderMeetings(upcoming, 'You have no upcoming meetings.')}
          </section>
          <section className="meetings-group" aria-labelledby="previous-meetings">
            <h2 id="previous-meetings">Previous meetings</h2>
            {renderMeetings(previous, 'You have no previous meetings.')}
          </section>
        </>
      )}
    </section>
  );
};

export default Meetings;
